import _ from "lodash";

export default function (
    $rootScope,
    $filter,
    SbStructureNode,
    $sbExternalService
) {
    "ngInject";

    $rootScope.$on("sb.global.logout", clearCache);

    ////////////////////////////////////
    //
    //      API
    //
    ////////////////////////////////////

    return {
        list: getList,
        tree: getTree,
        node: getNode,
        asMap: asMap,
        clearCache: clearCache,
        getSortedMappedList: getSortedMappedList,
    };

    ////////////////////////////////////
    //
    //      IMPL
    //
    ////////////////////////////////////

    function getTree(projectId, childrenPropertyName = "CHILDREN") {
        return getList(projectId).then(
            _buildTreeFromList(childrenPropertyName)
        );
    }

    function getNode(projectId, id) {
        return getList(projectId).then((nodes) => _.find(nodes, ["ID", id]));
    }

    function getList(projectId) {
        return $sbExternalService
            .structuresService()
            .getCollection()
            .then((structures) => _parse(structures, projectId))
            .then(_buildPathFromList)
            .then(_sortNaturally);
    }

    function asMap(projectId) {
        return getList(projectId).then((nodes) => {
            return new Map(nodes.map((item) => [item.ID, toDomain(item)]));
        });
    }

    function toDomain(dbNode) {
        return new SbStructureNode(
            dbNode.ID,
            dbNode.NAME,
            dbNode.CODE,
            dbNode.PARENT_PATH
        );
    }

    /**
     * Loads the list and does a natural sorting and then maps the result,
     * including the readable path
     * @param {String} projectId - Project Id
     * @returns {*}
     */
    function getSortedMappedList(projectId) {
        return getList(projectId).then(function (sortedNodes) {
            return sortedNodes.map(function (sNode) {
                return {
                    id: sNode.ID,
                    name: sNode.NAME,
                    label: sNode.PARENT_PATH.join(" / "),
                    code: sNode.CODE,
                    content: JSON.stringify([2, sNode.ID]),
                    isLeaf: sNode.LEFT_TREE_SPAN + 1 === sNode.RIGHT_TREE_SPAN,
                };
            });
        });
    }

    // entity of type web-apps/libs/shared/services/src/lib/domain/entities/structure.ts
    //
    function _parse(entity, projectId) {
        if (_.isArray(entity)) {
            return entity.map((e) => _parse(e, projectId));
        }
        return {
            ID: entity.id,
            PARENT_ID: entity.parent?.id,
            PROJECT_ID: projectId,
            NAME: entity.name,
            CODE: entity.code,
            LEFT_TREE_SPAN: entity.leftTreeSpan,
            RIGHT_TREE_SPAN: entity.rightTreeSpan,
        };
    }

    function _buildTreeFromList(childPropertyKey) {
        return function (nodes) {
            const roots = [];
            const idToNodeRegistry = {};

            nodes.forEach(function buildRegistry(node) {
                idToNodeRegistry[node.ID] = node;
                if (!node.PARENT_ID) {
                    roots.push(node);
                }
            });

            nodes.forEach(function addToParent(node) {
                // get parent
                const parent = idToNodeRegistry[node.PARENT_ID];
                if (parent) {
                    if (!parent[childPropertyKey]) {
                        parent[childPropertyKey] = [];
                    }
                    parent[childPropertyKey].push(node);
                }
            });

            return roots;
        };
    }

    function _sortNaturally(structureNodes) {
        const naturalSort = $filter("naturalSort");
        return naturalSort(structureNodes, "CODE");
    }

    function clearCache() {
        return $sbExternalService.structuresService().invalidateCache();
    }

    /**
     * Given a list of nodes with ID, PARENT_ID and NAME. It's adding a new property
     * "PARENT_PATH" to each node representing the path from itself to the root.
     *
     * Like: Node 12 / Building 2 / Area AE
     *
     * It also adds "PARENT_PATH_SEGMENTS", the same path but as {id, label} pairs
     * (root-first, including the node itself), so callers that need to link back
     * to an ancestor (e.g. breadcrumb navigation) don't have to re-derive the id
     * from the label - it's already known here while the path is resolved.
     *
     * @param {Array} nodes
     * @returns {Array} the same nodes that are given into the function but with the additional properties.
     * @private
     */
    function _buildPathFromList(nodes) {
        const queue = [];

        const nodesMap = nodes.reduce(function (map, node) {
            // create the map
            map[node.ID] = node;
            // add the additional property for the path and prepare the "recursive" calculation
            node.PARENT_PATH = [node.NAME];
            if (node.PARENT_ID) {
                node.PARENT_PATH.push(node.PARENT_ID);
                queue.push(node);
            }
            return map;
        }, {});

        // prepare "recursion"
        let nodeFromQueue = queue.pop();
        while (nodeFromQueue) {
            const parentIdOfPath = nodeFromQueue.PARENT_PATH.pop();
            const parentNode = nodesMap[parentIdOfPath];

            if (parentIdOfPath && parentNode) {
                nodeFromQueue.PARENT_PATH.push(parentNode.NAME);
                nodeFromQueue.PARENT_PATH.push(parentNode.PARENT_ID);
                queue.push(nodeFromQueue);
            }
            nodeFromQueue = queue.pop();
        }

        nodes.forEach(function (node) {
            node.PARENT_PATH = node.PARENT_PATH.reverse();
            node.PARENT_PATH_SEGMENTS = _buildPathSegments(node, nodesMap);
        });

        return nodes;
    }

    /**
     * Walks the PARENT_ID chain of a node (using the id -> node lookup already
     * built while resolving PARENT_PATH) and returns the ancestor chain,
     * root-first and including the node itself, as {id, label} pairs.
     *
     * @param {Object} node
     * @param {Object} nodesMap - id -> node lookup
     * @returns {Array<{id: String, label: String}>}
     * @private
     */
    function _buildPathSegments(node, nodesMap) {
        const segments = [{ id: node.ID, label: node.NAME }];

        let parentNode = nodesMap[node.PARENT_ID];
        while (parentNode) {
            segments.unshift({ id: parentNode.ID, label: parentNode.NAME });
            parentNode = nodesMap[parentNode.PARENT_ID];
        }

        return segments;
    }
}
